// apiDocs.js — static content for Settings → API & Webhooks (docs/api-webhooks-spec.md §6, catalog per §1,
// inbound API per §5). This is the SAME catalog JSON `manage-webhooks` action `catalog` serves; keep the two
// in step when an event is added. Pure data + string builders — no network, no React.
//
// Exports:
//   API_VERSION, SCOPES, EVENT_CATALOG, exampleEnvelope(event), API_ENDPOINTS, apiBaseUrl(supabaseUrl),
//   curlExample(baseUrl, ep), NODE_VERIFY_SNIPPET, PYTHON_VERIFY_SNIPPET, GHL_CONNECT_STEPS, GHL_REVERSE_STEPS,
//   GHL_NOTE, isPublicHttpsUrl(url)

export const API_VERSION = "2026-10-23";

// Inbound API key scopes (spec §4): read = GET endpoints, write = POST job actions, onboarding = api-onboard-client.
export const SCOPES = [
  { key: "read",       label: "Read",       desc: "Clients, jobs, technicians, today's route, event log (GET)." },
  { key: "write",      label: "Write",      desc: "Trigger job actions: on the way, start, complete, skip, reschedule, reassign (POST)." },
  { key: "onboarding", label: "Onboarding", desc: "Create clients through the partner onboarding endpoint (api-onboard-client)." },
];

// ── Example objects (spec §1 "Objects") — one definition so every event example agrees ─────────────
const EX_ORG  = { id: "6d1f0c9e-1b2a-4c3d-9e8f-0a1b2c3d4e5f", name: "Scoopers on Doody", slug: "scoopers-on-doody" };
const EX_TECH = { id: "b3c2d1e0-9f8a-4b7c-8d6e-5f4a3b2c1d0e", name: "Ruben M.", phone: "+15125550142" };
const EX_CLIENT = {
  id: "0f9e8d7c-6b5a-4c3d-2e1f-0a9b8c7d6e5f",
  first_name: "Jordan", last_name: "Lee", name: "Jordan Lee",
  email: "jordan@example.com", phone: "+15125550199", phone_secondary: null,
  address: "1400 Maple Ave", city: "Austin", state: "TX", zip: "78704", lat: 30.2449, lng: -97.7602,
  dogs: 2, frequency: "weekly", status: "active", active: true,
  deo_enabled: false, deo_frequency: null,
  technician: { id: EX_TECH.id, name: EX_TECH.name },
  gate_code: "1234", access_notes: "Side gate, latch sticks", notes: null,
  created_at: "2026-03-02T16:40:00Z",
};
const EX_SERVICE = { id: "4a5b6c7d-8e9f-4a1b-9c2d-3e4f5a6b7c8d", service_type: "cleanup", status: "completed", skipped_reason: null, skip_note: null, credited: false };
const EX_JOB = {
  id: "c1d2e3f4-a5b6-4c7d-8e9f-0a1b2c3d4e5f",
  scheduled_date: "2026-10-23", status: "completed", route_seq: 4,
  started_at: "2026-10-23T13:48:02Z", completed_at: "2026-10-23T14:02:11Z", completion_notified_at: "2026-10-23T14:02:14Z",
  photo_url: "https://wfvjmihvtfcdnoayihue.supabase.co/storage/v1/object/sign/job-photos/…?token=…", has_photo: true,
  services: [EX_SERVICE],
  technician: { id: EX_TECH.id, name: EX_TECH.name },
  client_id: EX_CLIENT.id,
};
const jobAs = (status, extra = {}) => ({ ...EX_JOB, status, ...extra });
const scheduledJob = jobAs("scheduled", { started_at: null, completed_at: null, completion_notified_at: null, photo_url: null, has_photo: false, services: [{ ...EX_SERVICE, status: "scheduled" }] });

// ── Event catalog (spec §1) — order is the order the Settings checklist and docs render ─────────────
export const EVENT_CATALOG = [
  { event: "job.on_the_way",  label: "Tech on the way",      group: "Jobs",
    desc: "A technician sent the ETA text for a client (one event per send, whatever the channel).",
    data: { eta_minutes: 15, technician: EX_TECH, client: EX_CLIENT, job: scheduledJob } },
  { event: "job.started",     label: "Job started",          group: "Jobs",
    desc: "The technician marked the visit as started (arrived on site).",
    data: { job: jobAs("started", { completed_at: null, completion_notified_at: null, photo_url: null, has_photo: false, services: [{ ...EX_SERVICE, status: "started" }] }), technician: EX_TECH, client: EX_CLIENT } },
  { event: "job.completed",   label: "Job completed",        group: "Jobs",
    desc: "Every service on the visit is done. photo_url is a 7-day signed link when the tech attached a photo.",
    data: { job: EX_JOB, technician: EX_TECH, client: EX_CLIENT } },
  { event: "job.skipped",     label: "Job skipped",          group: "Jobs",
    desc: "The visit was skipped. skip_reason is one of unsafe_dog, locked_gate, bad_weather, yard_condition, cant_find_location, client_request, non_payment, holiday, other; skip_note is the tech's optional free text (or null). skip_kind buckets the reason (access, dogs_loose, weather, client…).",
    data: { skip_reason: "locked_gate", skip_note: "Side gate padlocked, texted owner", skip_kind: "access", job: jobAs("skipped", { completed_at: null, completion_notified_at: null, photo_url: null, has_photo: false, services: [{ ...EX_SERVICE, status: "skipped", skipped_reason: "locked_gate", skip_note: "Side gate padlocked, texted owner" }] }), technician: EX_TECH, client: EX_CLIENT } },
  { event: "job.scheduled",   label: "Job scheduled",        group: "Jobs",
    desc: "A visit was put on the calendar (new job header, or an empty header became scheduled).",
    data: { job: scheduledJob, technician: EX_TECH, client: EX_CLIENT } },
  { event: "job.rescheduled", label: "Job rescheduled",      group: "Jobs",
    desc: "A visit moved to a new date. rescheduled_to / new_job_id point at the replacement when known.",
    data: { job: jobAs("rescheduled", { completed_at: null, completion_notified_at: null, photo_url: null, has_photo: false }), rescheduled_to: "2026-10-25", new_job_id: "d9e8f7a6-b5c4-4d3e-2f1a-0b9c8d7e6f5a", technician: EX_TECH, client: EX_CLIENT } },
  { event: "job.reassigned",  label: "Job reassigned",       group: "Jobs",
    desc: "A scheduled visit was handed to a different technician.",
    data: { job: scheduledJob, technician: EX_TECH, previous_technician: { id: "7e6d5c4b-3a2f-4e1d-9c8b-7a6f5e4d3c2b", name: "Alex P.", phone: "+15125550177" }, client: EX_CLIENT } },
  { event: "client.created",  label: "Client created",       group: "Clients",
    desc: "A client record was created (signup page, import, API, or by hand).",
    data: { client: EX_CLIENT } },
  { event: "client.updated",  label: "Client updated",       group: "Clients",
    desc: "A client's status, plan, contact or address details changed. changes lists each field with old/new text.",
    data: { client: { ...EX_CLIENT, status: "paused", active: false }, changes: [{ field: "status", old: "active", new: "paused" }, { field: "active", old: "true", new: "false" }] } },
  { event: "payment.failed",  label: "Payment failed",       group: "Billing",
    desc: "A client's card was declined on an invoice. attempt counts Stripe's retries.",
    data: { client: EX_CLIENT, invoice: { stripe_invoice_id: "in_1QAbCdEfGhIjKlMn", amount: 89.0, attempt: 2, hosted_url: "https://invoice.stripe.com/i/acct_…/…" } } },
  { event: "invoice.paid",    label: "Invoice paid",         group: "Billing",
    desc: "A client's invoice was paid.",
    data: { client: EX_CLIENT, invoice: { stripe_invoice_id: "in_1QAbCdEfGhIjKlMn", amount: 89.0, paid_at: "2026-10-01T15:12:40Z" } } },
  { event: "test.ping",       label: "Test ping",            group: "System",
    desc: "Sent when you press Test on an endpoint. Not selectable — every endpoint receives it.",
    data: { message: "ScoopCRM webhook test", endpoint_id: "2a3b4c5d-6e7f-4a8b-9c0d-1e2f3a4b5c6d" }, system: true },
];

// Events an operator can subscribe to (test.ping is implicit).
export const SUBSCRIBABLE_EVENTS = EVENT_CATALOG.filter((e) => !e.system).map((e) => e.event);

// Full delivery body for one event (spec §1 "Envelope").
export function exampleEnvelope(event) {
  const e = EVENT_CATALOG.find((x) => x.event === event) || EVENT_CATALOG[0];
  return {
    id: "9b8a7c6d-5e4f-4a3b-2c1d-0e9f8a7b6c5d",
    event: e.event,
    created_at: "2026-10-23T14:02:11Z",
    api_version: API_VERSION,
    organization: EX_ORG,
    data: e.data,
  };
}

// ── Inbound API (spec §5) ──────────────────────────────────────────────────────────────────────────
export function apiBaseUrl(supabaseUrl) {
  return `${(supabaseUrl || "https://wfvjmihvtfcdnoayihue.supabase.co").replace(/\/$/, "")}/functions/v1/api-v1`;
}

export const API_ENDPOINTS = [
  { method: "GET",  path: "/me",                       scope: "read",  desc: "Who am I — organization, key label and scopes." },
  { method: "GET",  path: "/clients",                  scope: "read",  desc: "List clients. Query: status, q (name/phone/email/address search), limit (≤200), offset." , query: "?status=active&limit=50" },
  { method: "GET",  path: "/clients/{id}",             scope: "read",  desc: "One client plus upcoming_jobs (next 5 visits)." },
  { method: "GET",  path: "/technicians",              scope: "read",  desc: "Your technicians: id, name, phone, active." },
  { method: "GET",  path: "/jobs",                     scope: "read",  desc: "Visits for a day (default today, Eastern). Query: date=YYYY-MM-DD, status, technician_id, client_id, limit, offset.", query: "?date=2026-10-23" },
  { method: "GET",  path: "/jobs/{id}",                scope: "read",  desc: "One visit with its client." },
  { method: "GET",  path: "/route/today",              scope: "read",  desc: "Today's route per technician: ordered stops with client, leg minutes and miles. Query: technician_id, date." },
  { method: "GET",  path: "/events",                   scope: "read",  desc: "Your recent events (same bodies as the webhooks) for polling. Query: limit, cursor." },
  { method: "POST", path: "/jobs/{id}/on-the-way",     scope: "write", desc: "Send the client the same on-the-way / ETA text a tech's tap sends, from the CRM number. Today's scheduled or in-progress jobs only; the technician name comes from the job (never from the request).", body: { minutes: 15 } },
  { method: "POST", path: "/jobs/{id}/start",          scope: "write", desc: "Mark the visit started (every scheduled service on it).", body: {} },
  { method: "POST", path: "/jobs/{id}/complete",       scope: "write", desc: "Complete the visit — sends the usual all-done text and fires job.completed.", body: {} },
  { method: "POST", path: "/jobs/{id}/skip",           scope: "write", desc: "Skip the visit. reason is one of the 9 skip codes (e.g. locked_gate); skip_note is optional free text. Does not apply a credit automatically — do that in the CRM if you want one.", body: { reason: "locked_gate", skip_note: "Side gate padlocked" } },
  { method: "POST", path: "/jobs/{id}/reschedule",     scope: "write", desc: "Move the visit to another date.", body: { date: "2026-10-25" } },
  { method: "POST", path: "/jobs/{id}/reassign",       scope: "write", desc: "Hand the visit to another technician.", body: { technician_id: EX_TECH.id } },
];

export const API_ERRORS = [
  ["401", "invalid_api_key",    "Missing, revoked, expired or unknown key."],
  ["403", "insufficient_scope", "The key lacks the scope this endpoint needs."],
  ["404", "not_found",          "No such record in your organization (ids from other operators look identical)."],
  ["422", "validation",         "A field is missing or malformed — the message says which."],
  ["429", "rate_limited",       "Over the key's per-minute allowance. Honour Retry-After."],
];

export function curlExample(baseUrl, ep) {
  const path = ep.path.replace("{id}", "JOB_OR_CLIENT_ID") + (ep.query || "");
  if (ep.method === "GET") return `curl "${baseUrl}${path}" \\\n  -H "Authorization: Bearer sk_scoop_YOUR_KEY"`;
  return `curl -X POST "${baseUrl}${path}" \\\n  -H "Authorization: Bearer sk_scoop_YOUR_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(ep.body || {})}'`;
}

// ── Signature verification (spec §3 headers) ────────────────────────────────────────────────────────
export const SIGNATURE_HEADERS = [
  ["X-ScoopCRM-Event",       "the event name, e.g. job.completed"],
  ["X-ScoopCRM-Delivery-Id", "unique per delivery attempt group — use it to de-duplicate retries"],
  ["X-ScoopCRM-Endpoint-Id", "which endpoint this went to"],
  ["X-ScoopCRM-Timestamp",   "unix seconds when the request was signed"],
  ["X-ScoopCRM-Signature",   "t=<unix>,v1=<hex HMAC-SHA256(secret, \"<unix>.<raw body>\")>"],
];

export const NODE_VERIFY_SNIPPET = `// Express — keep the RAW body (express.raw) so the bytes match what we signed.
const crypto = require("crypto");
const SECRET = process.env.SCOOPCRM_WEBHOOK_SECRET; // whsec_…

function verify(req) {
  const sig = req.get("X-ScoopCRM-Signature") || "";
  const parts = Object.fromEntries(sig.split(",").map(p => p.split("=")));
  const t = parts.t, v1 = parts.v1;
  if (!t || !v1) return false;
  if (Math.abs(Date.now() / 1000 - Number(t)) > 300) return false;  // 5-minute tolerance
  const expected = crypto.createHmac("sha256", SECRET)
    .update(\`\${t}.\${req.body.toString("utf8")}\`).digest("hex");
  return expected.length === v1.length &&
    crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(v1));
}

app.post("/scoopcrm", express.raw({ type: "application/json" }), (req, res) => {
  if (!verify(req)) return res.status(401).end();
  const evt = JSON.parse(req.body);
  // evt.event, evt.data.client, evt.data.job …
  res.status(200).end();   // answer 2xx fast; do the work after
});`;

export const PYTHON_VERIFY_SNIPPET = `# Flask — request.get_data() returns the raw bytes we signed.
import hmac, hashlib, time
from flask import Flask, request, abort

SECRET = b"whsec_..."   # from Settings → API & Webhooks
app = Flask(__name__)

def verify(req):
    parts = dict(p.split("=", 1) for p in req.headers.get("X-ScoopCRM-Signature", "").split(",") if "=" in p)
    t, v1 = parts.get("t"), parts.get("v1")
    if not t or not v1 or abs(time.time() - int(t)) > 300:
        return False
    expected = hmac.new(SECRET, f"{t}.".encode() + req.get_data(), hashlib.sha256).hexdigest()
    return hmac.compare_digest(expected, v1)

@app.post("/scoopcrm")
def hook():
    if not verify(request):
        abort(401)
    evt = request.get_json()
    # evt["event"], evt["data"]["client"], evt["data"]["job"] ...
    return "", 200`;

// ── GoHighLevel how-tos (spec §6 item 5) ──────────────────────────────────────────────────────────
export const GHL_CONNECT_STEPS = [
  ["In GoHighLevel", "Go to Automation → Workflows → Create Workflow → Start from scratch."],
  ["Add the trigger", "Add New Trigger → Inbound Webhook. GHL shows a unique URL — copy it."],
  ["Add it here", "Under Webhooks press Add endpoint, paste that URL, and tick the events you want (start with Tech on the way, Job completed, Job skipped)."],
  ["Test it", "Press Test on the endpoint. GHL's trigger will show the sample payload so you can map fields."],
  ["Map the fields", "In the GHL trigger, map data.client.phone, data.client.first_name, data.eta_minutes and data.technician.name to custom values (and data.client.email if you want to match contacts by email)."],
  ["Add actions", "Add actions like Send SMS from your GHL number, Add Tag, or Move Pipeline Stage. Save & Publish the workflow."],
  ["Go live", "Once mapped, fire a real event (e.g. complete a job) and watch Recent deliveries here and the workflow's execution log in GHL."],
];

export const GHL_NOTE = "GHL's Inbound Webhook is a premium trigger: after the free monthly allowance it costs about $0.01 per execution. Subscribe only to the events you actually use.";

export const GHL_REVERSE_STEPS = [
  ["Create a key here", "Under API keys press Create key with the Write scope (add Read if the workflow also needs to look things up). Copy it — it is shown once."],
  ["Store it in GHL", "Settings → Custom Values → add SCOOPCRM_API_KEY with the key, so it never sits in a workflow step."],
  ["Call the CRM", "In a workflow add the Webhook action (Custom Webhook): method POST, URL <API base>/jobs/{job id}/skip, header Authorization: Bearer {{custom_values.scoopcrm_api_key}}, header Content-Type: application/json, body {\"reason\": \"Customer requested\"}."],
  ["Get the job id", "Take it from a ScoopCRM event you received earlier (data.job.id), or look it up with GET /jobs?client_id=… from a prior Webhook step."],
  ["Check the log", "Every call shows under Recent API calls with its status code."],
];

// ── Client-side URL guard (manage-webhooks is authoritative; this just saves a round trip) ─────────
const PRIVATE_HOST = /^(localhost|127\.|10\.|0\.|169\.254\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.|\[?::1\]?$|\[?fc|\[?fd|\[?fe80)/i;
export function isPublicHttpsUrl(raw) {
  let u;
  try { u = new URL(String(raw || "").trim()); } catch { return false; }
  if (u.protocol !== "https:") return false;
  const h = u.hostname.toLowerCase();
  if (!h.includes(".") && !h.startsWith("[")) return false;            // bare names (intranet) are never public
  if ([".local", ".internal", ".localhost", ".home", ".lan", ".corp", ".intranet", ".onion", ".test", ".invalid", ".example"].some((suf) => h.endsWith(suf))) return false;
  if (h.startsWith("[") || h.includes(":")) return false;              // IPv6 literals are rejected server-side too
  if (PRIVATE_HOST.test(h)) return false;
  return true;
}
